import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";

// Retain the existing preference key so rebranding does not erase saved pincodes.
const KEY = "terramart_delivery_pincode_v1";
const DeliveryContext = createContext(null);

export function DeliveryProvider({ children }) {
  const [pincode, setPincodeState] = useState(() => localStorage.getItem(KEY) || "");
  const [pincodes, setPincodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const refreshDelivery = useCallback(async () => {
    try { const { data } = await api.get("/delivery/config"); setPincodes(data.pincodes); setError(""); return data.pincodes; }
    catch { setPincodes([]); setError("We couldn't check delivery right now. Please retry."); return null; }
    finally { setLoading(false); }
  }, []);
  useEffect(() => {
    refreshDelivery(); window.addEventListener("focus", refreshDelivery);
    return () => window.removeEventListener("focus", refreshDelivery);
  }, [refreshDelivery]);
  useEffect(() => {
    if (pincode) localStorage.setItem(KEY, pincode);
    else localStorage.removeItem(KEY);
  }, [pincode]);
  const isPincodeServiceable = useCallback((pin) => !error && pincodes.includes(String(pin).trim()), [pincodes, error]);
  const isServiceable = isPincodeServiceable(pincode);
  const setPincode = useCallback((p) => setPincodeState((p || "").replace(/\D/g, "").slice(0, 6)), []);
  const clearPincode = useCallback(() => setPincodeState(""), []);
  const checkPincode = useCallback(async (pin) => {
    const codes = await refreshDelivery();
    if (codes === null) throw new Error("We couldn't check delivery right now. Please retry.");
    return codes.includes(String(pin).trim());
  }, [refreshDelivery]);
  const value = useMemo(() => ({ pincode, isServiceable, isPincodeServiceable, setPincode, clearPincode, checkPincode, refreshDelivery, loading, error }), [pincode, isServiceable, isPincodeServiceable, setPincode, clearPincode, checkPincode, refreshDelivery, loading, error]);
  return <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>;
}
export const useDelivery = () => useContext(DeliveryContext);
